<template>
  <div class="v3-widget v3-panel" v-testid="testid">
    <div class="v3-panel-head">
      <h3 v-testid="testid + '-title'">{{ title }}</h3>
      <span v-if="tl.filtered.value.length" class="v3-count">{{ tl.filtered.value.length }}</span>
      <span class="spacer"></span>
      <button
        class="v3-chipbtn"
        :class="{ 'is-on': tl.groupBy.value !== 'due' }"
        type="button"
        title="Group rows by due date"
        v-testid="testid + '-group-toggle'"
        @click="tl.groupBy.value = tl.groupBy.value === 'due' ? 'none' : 'due'"
      >
        <span class="ms">layers</span> Group: {{ tl.groupBy.value === 'due' ? 'Due date' : 'None' }}
      </button>
      <span v-if="tl.searchOpen.value" class="v3-tl-search">
        <span class="ms">search</span>
        <input v-model="tl.q.value" placeholder="Search tasks…" aria-label="Search tasks" v-testid="testid + '-search'" />
      </span>
      <button v-else class="v3-chipbtn" type="button" title="Search" v-testid="testid + '-search-open'" @click="tl.searchOpen.value = true">
        <span class="ms">search</span>
      </button>
    </div>

    <V3Skeleton v-if="tl.isLoading.value && !tl.taskCount.value" shape="list" :heading="false" />
    <div v-else-if="!tl.taskCount.value" class="v3-task-empty" v-testid="testid + '-empty'">You're all caught up — no open tasks.</div>

    <template v-for="g in tl.displayGroups.value">
      <div :key="g.id">
        <button v-if="!g.noHead" class="v3-group-head" type="button" :aria-expanded="!tl.isCollapsed(g)" v-testid="testid + '-group-' + g.id" @click="tl.toggle(g.id)">
          <span class="lbl">{{ g.label }}</span>
          <span class="n">{{ g.items.length }}</span>
          <span class="ms">{{ tl.isCollapsed(g) ? 'chevron_right' : 'expand_more' }}</span>
        </button>
        <button
          v-if="!tl.isCollapsed(g) && tl.isExpanded(g.id) && g.items.length > limit"
          class="v3-viewall v3-viewall--top"
          type="button"
          v-testid="testid + '-group-' + g.id + '-show-less'"
          @click="tl.toggleExpand(g.id)"
        >
          <span class="ms">expand_less</span> Show less
        </button>
        <div
          v-for="t in tl.shown(g)"
          :key="t.document_task_id || t.reference"
          class="v3-trow"
          role="button"
          tabindex="0"
          v-testid="testid + '-row-' + (t.document_task_id || t.reference)"
          @click="open(t)"
        >
          <span class="v3-stic" :title="tl.stepText(t) || tl.statusText(t)">
            <svg v-if="tl.statusVisual(t) === 'progress'" width="18" height="18" viewBox="0 0 18 18">
              <circle cx="9" cy="9" r="7" fill="none" stroke="var(--v3-border)" stroke-width="2.5"></circle>
              <circle
                cx="9"
                cy="9"
                r="7"
                fill="none"
                stroke="var(--v3-accent)"
                stroke-width="2.5"
                stroke-linecap="round"
                :stroke-dasharray="tl.STIC_CIRC"
                :stroke-dashoffset="tl.STIC_CIRC * (1 - tl.stepFrac(t))"
                transform="rotate(-90 9 9)"
              ></circle>
            </svg>
            <span v-else-if="tl.statusVisual(t) === 'waiting'" class="ms">hourglass_top</span>
            <svg v-else width="18" height="18" viewBox="0 0 18 18">
              <circle cx="9" cy="9" r="7" fill="none" stroke="var(--v3-text-3)" stroke-width="2" stroke-dasharray="2.5 3"></circle>
            </svg>
          </span>
          <div class="v3-tcell v3-tcell--name">
            <div class="v3-row-title">{{ t.doc_name || t.reference }}<span v-if="t.reference" class="ref">{{ t.reference }}</span></div>
            <div v-if="tl.rowSub(t)" class="v3-row-sub">{{ tl.rowSub(t) }}</div>
          </div>
          <div class="v3-tcell v3-tcell--type"><span class="v3-chip">{{ t.task_type }}</span></div>
          <div class="v3-tcell v3-tcell--due" :class="tl.dueClass(t)">{{ tl.dueText(t) || '—' }}</div>
          <div class="v3-tcell v3-tcell--status">
            <span class="v3-task-pill" :style="{ '--status-c': t.task_status_colour || '#5A6678' }">{{ t.task_status }}</span>
          </div>
          <div class="v3-tcell v3-tcell--act">
            <button class="v3-tact" type="button" @click.stop="open(t)">{{ tl.actionLabel(t) }}</button>
          </div>
        </div>
        <button v-if="!tl.isCollapsed(g) && g.items.length > limit" class="v3-viewall" type="button" v-testid="testid + '-group-' + g.id + '-view-all'" @click="tl.toggleExpand(g.id)">
          {{ tl.isExpanded(g.id) ? 'Show less' : 'View all (' + g.items.length + ')' }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { getCurrentInstance } from "vue";
import { taskList } from "@rm-ui/homepage/taskList";
import V3Skeleton from "./cLandingV3Skeleton";

// New-portal (v3) MyTasks — thin Vue 2 presentation over the shared `taskList`
// composable (@rm-ui/homepage/taskList). All grouping/status/due logic lives in
// the composable; this file owns only the Vuex wiring (via setup) and document
// navigation. Sibling presentation: rm-portal-beta TaskListWidget.vue.
export default {
  name: "cLandingV3TaskList",
  components: { V3Skeleton },
  props: {
    definition: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
  },
  setup(props) {
    const store = getCurrentInstance().proxy.$store;
    const tl = taskList({
      getTasks: () => store.state.tasks.taskList,
      getLoading: () => store.state.tasks.isLoading,
      getLimit: () => props.definition.limit || 5,
      getDocTypes: () => props.definition.docTypes || [],
    });
    return { tl };
  },
  computed: {
    testid() {
      return this.definition.controlType || "v3TaskList";
    },
    title() {
      return this.definition.title || "My Tasks";
    },
    limit() {
      return this.definition.limit || 5;
    },
  },
  created() {
    this.$store.dispatch("tasks/init");
  },
  methods: {
    open(t) {
      if (this.editMode) return;
      this.$emit("openDocument", t);
    },
  },
};
</script>

<style lang="scss" src="./v3Widgets.scss"></style>
